import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { Button } from 'app/components/ui/button'
import FormComboAsyncV2 from 'app/components/form/FormComboAsyncV2'
import FormInput from 'app/components/form/FormInput'
import { FormPanelShadcnComponent } from 'app/components/form-panel/FormPanelShadcnComponent'
import { SmartForm } from 'app/components/form/SmartForm'
import { LearnMoreLink } from 'app/components/shared/LearnMoreLink'
import UserLocationSelector from 'app/components/UserLocationSelector'
import { RoleSelector } from 'app/components/users/RoleSelector'
import { legacyRoleRank } from 'app/types/roles'
import type { RoleJson } from 'app/types/entities'
import type { StaffJson } from 'app/types/entities'
import type { OptionType } from 'types/OptionType'
import { createGqlFetcher } from 'app/lib/gqlFetcher'

export type UserInviteShadcnComponentProps = {
  me: { orgId: string }
  currentWorkspaceId: string
  workspaceRoles: RoleJson[]
  workspaceId: string | undefined
  isDirtyForm: boolean
  setIsDirtyForm: (v: boolean) => void
  role: number
  roleId: string
  selectedStaff: OptionType | undefined
  locationInputs: Array<{ name: string; value: string }>
  setLocationInputs: (v: Array<{ name: string; value: string }>) => void
  onRoleChange: (newRole: number | undefined, newRoleId: string | undefined) => void
  onStaffChange: (option: OptionType) => void
  openAddStaffModal: () => void
  placeholderUser: { id: string; allowedLocationIds: string[]; workspaceId: string | undefined }
  validator?: any
  onSubmit?: (data?: Record<string, unknown>) => void
}

export const UserInviteShadcnComponent = observer(function UserInviteShadcnComponent({
  me,
  currentWorkspaceId,
  workspaceRoles,
  workspaceId,
  isDirtyForm,
  setIsDirtyForm,
  role,
  roleId,
  selectedStaff,
  locationInputs,
  setLocationInputs,
  onRoleChange,
  onStaffChange,
  openAddStaffModal,
  placeholderUser,
  validator,
  onSubmit,
}: UserInviteShadcnComponentProps): ReactElement {
  return (
    <SmartForm
      id="invite-form"
      validator={validator}
      method="post"
      className="!mt-0"
      hideButtons={true}
      onSubmit={onSubmit}
      hiddenInputs={[{ name: `roleId`, value: roleId || `` }]}
    >
      {selectedStaff && <input type="hidden" name="staffId" value={selectedStaff.id} />}

      {locationInputs.map((input, idx) => (
        <input key={idx} type="hidden" name={input.name} value={input.value} />
      ))}

      {/* User Details */}
      <FormPanelShadcnComponent title="User Details" cols={6}>
        <div className="grid gap-4">
          <FormInput
            name="name"
            label="Name"
            data-testid="invite-user-name"
            required
            labelClassName="required text-sm font-medium text-default"
            className="text-sm text-strong"
            onChange={() => setIsDirtyForm(true)}
          />
          <FormInput
            name="email"
            label="Email"
            type="email"
            data-testid="invite-user-email"
            required
            labelClassName="required text-sm font-medium text-default"
            className="text-sm text-strong"
            onChange={() => setIsDirtyForm(true)}
          />
        </div>
      </FormPanelShadcnComponent>

      {/* Permissions */}
      <FormPanelShadcnComponent
        title="Permissions"
        cols={6}
        learnMore={
          <LearnMoreLink to="https://intercom.help/trackable/en/articles/11643458-user-permissions">
            View Permissions
          </LearnMoreLink>
        }
      >
        <div className="grid grid-cols-6 gap-4">
          <RoleSelector
            workspaceRoles={workspaceRoles}
            role={role}
            roleId={roleId}
            onChange={onRoleChange}
            setIsDirtyForm={setIsDirtyForm}
          />
          <UserLocationSelector
            user={placeholderUser}
            role={legacyRoleRank.workspaceUser}
            isNewUser={true}
            isNewWorkspaceUser={true}
            isOrgLevel={false}
            staffV2Enabled={true}
            setIsDirtyForm={setIsDirtyForm}
            onHiddenInputsChange={setLocationInputs}
          />
        </div>
      </FormPanelShadcnComponent>

      {/* Linked Staff */}
      <FormPanelShadcnComponent
        title="Linked Staff"
        cols={6}
        learnMore={
          <LearnMoreLink to="https://intercom.help/trackable/en/articles/11643458-user-permissions">
            Learn About Staff
          </LearnMoreLink>
        }
      >
        <FormComboAsyncV2
          name="_staffDisplay"
          labelClassName="text-sm text-subtle"
          inputClassName="text-sm text-strong"
          optionsMapper={(res) =>
            res.hits.map((hit: StaffJson) => ({
              id: hit.id!,
              name: hit.name!,
              value: hit.id!,
            }))
          }
          fetcher={createGqlFetcher('users')}
          additionalParams={{ unassignedOnly: `true`, onlyUsers: `true`, workspaceId: workspaceId ?? `` }}
          value={selectedStaff}
          onChange={onStaffChange as any}
          placeholder="No Linked Staff"
          onAddNewValue={openAddStaffModal}
          addText="Add New Staff"
        />
      </FormPanelShadcnComponent>

      {/* Floating save */}
      <div className="h-40" />
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-default bg-surface px-6 py-4">
        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={!isDirtyForm} data-testid="invite-user-submit">
            Send Invite
          </Button>
        </div>
      </div>
    </SmartForm>
  )
})
